// src/utils/Hote.js
import axios from "axios";
import moment from "moment";


// const API_BASE_URL = "http://service.paximum.com/api";

// ✅ TourVisio Credentials
const TOURVISIO_CREDENTIALS = {
  Agency: "PXM109476",
  User: "USR163895",
  Password: "K0205*"
};

const API_BASE_URL = "https://service.paximum.com/v2/api";

// const TOURVISIO_CREDENTIALS = {
//   Agency: "PXM25742",
//   User: "USR1",
//   Password: "Admin01."
// };

let token = null;
let tokenExpiry = null;
let authPromise = null;


const hotelOffersCache = new Map();
const hotelOffersCacheCount = new Map();
const CACHE_INVALIDATE_COUNT = 5;

const GLOBAL_PRICE_MARKUP = 0.15;

const applyPriceMarkup = (amount) => {
  if (typeof amount !== "number" || isNaN(amount)) return amount;
  return +(amount * (1 + GLOBAL_PRICE_MARKUP)).toFixed(2);
};

function makeCacheKey({ cityId, adults, children = 0, currency = "EUR", checkInDate, checkOutDate }) {
  return [cityId, adults, children, currency, checkInDate, checkOutDate].join("|");
}

export const authenticateTourVisio = async () => {
  if (token && tokenExpiry && moment().isBefore(tokenExpiry)) {
    return token;
  }

  if (!authPromise) {
    authPromise = axios
      .post(`https://0nc70p8y1i.execute-api.eu-central-1.amazonaws.com/paximum/authenticate`)
      .then((response) => {
        
        // Handle Lambda response structure: response.data.data.body.token
        const lambdaToken = response.data?.data?.body?.token || response.data?.token;
        const lambdaExpiry = response.data?.data?.body?.expiresOn;
        
        if (lambdaToken) {
          token = lambdaToken;
          if (lambdaExpiry) {
            tokenExpiry = moment(lambdaExpiry);
          } else {
            // Default to 2 hours if no expiry provided
            tokenExpiry = moment().add(2, 'hours');
          }
          return token;
        }
        
        console.error("❌ No token found in Lambda response:", response.data);
        throw new Error("Failed to obtain token from Lambda - no token in response");
      })
      .catch((error) => {
        console.error("❌ Lambda Authentication Error:", error.response?.data || error.message);
        token = null;
        tokenExpiry = null;
        throw error;
      })
      .finally(() => {
        authPromise = null;
      });
  }

  return authPromise;
};

// ✅ Get City ID Using GetArrivalAutocomplete
export const getCityId = async (cityName, countryCode = "GB") => {
  const token = await authenticateTourVisio();
  if (!token) {
    console.warn(`⚠️ Token auth failed for city lookup: ${cityName}`);
    return [];
  }

  const payload = {
    ProductType: 2, // Hotels
    Query: cityName,
    Culture: "en-US"
  };

  try {
    const response = await axios.post(
      `${API_BASE_URL}/productservice/getarrivalautocomplete`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    const items = response.data.body?.items;
    if (!Array.isArray(items)) return [];

    return items
      .filter((item) => item.hotelCount > 0)
      .map((item) => ({
        ...item,
        name: item.city.name,
        country_name: item.country.name,
        country_code: item.country.id
      }));
  } catch (error) {
    console.error(`❌ Error fetching city ID for ${cityName}:`, error.response?.data || error.message);
    return [];
  }
};

// ✅ Handle City Selection Helper
export const handleCitySelection = async (
  selectedCity,
  setSelectedCity,
  setCityId,
  setCityLat,
  setCityLng
) => {
  if (!selectedCity) return;
  setSelectedCity(selectedCity);

  const apiCities = await getCityId(selectedCity.name, selectedCity.country_code);
  if (!apiCities.length) return;

  const matchedCity = apiCities.find(
    (city) =>
      city.city?.name.toLowerCase().includes(selectedCity.name.toLowerCase()) &&
      city.country_code?.toUpperCase() === selectedCity.country_code.toUpperCase()
  );
  if (!matchedCity) return;

  setCityId(matchedCity.city.id);
  setCityLat(matchedCity.geolocation?.latitude);
  setCityLng(matchedCity.geolocation?.longitude);
};

// ✅ Fetch Hotels for a City (with 5‑use cache)
export const fetchHotelOffers = async ({
  cityName,
  cityId,
  checkInDate,
  checkOutDate,
  adults,
  children = 0,
  childAges,
  nationality = "XK",
  currency = "EUR"
}) => {
  // determine check‑in/out if missing
  if (!checkInDate || !checkOutDate) {
    const today = moment();
    let daysUntilFriday = (5 - today.isoWeekday() + 7) % 7;
    if (daysUntilFriday === 0 || daysUntilFriday === 6) daysUntilFriday += 7;
    checkInDate = moment().add(daysUntilFriday, "days").format("YYYY-MM-DD");
    checkOutDate = moment().add(daysUntilFriday + 2, "days").format("YYYY-MM-DD");
  }
  const nights = moment(checkOutDate).diff(moment(checkInDate), "days") || 2;

  // build cache key
  const key = makeCacheKey({ cityId, adults, children, currency, checkInDate, checkOutDate });
  if (hotelOffersCache.has(key)) {
    // increment usage count
    const used = (hotelOffersCacheCount.get(key) || 0) + 1;
    if (used >= CACHE_INVALIDATE_COUNT) {
      hotelOffersCache.delete(key);
      hotelOffersCacheCount.delete(key);
    } else {
      hotelOffersCacheCount.set(key, used);
    }
    return hotelOffersCache.get(key);
  }

  // make sure we have a token
  const token = await authenticateTourVisio();
  if (!token) {
    console.warn(`⚠️ Auth failed. Skipping fetchHotelOffers for city ${cityName || cityId}`);
    return null;
  }

  // resolve cityId if only name was passed
  if (!cityId && cityName) {
    const ids = await getCityId(cityName);
    cityId = ids[0]?.city?.id;
    if (!cityId) {
      console.warn(`⚠️ No cityId found for ${cityName}`);
      return null;
    }
  }

  const payload = {
    checkAllotment: true,
    checkStopSale: true,
    getOnlyDiscountedPrice: false,
    getOnlyBestOffers: true,
    productType: 2,
    arrivalLocations: [{ id: cityId, type: 2 }],
    roomCriteria: [{ adult: adults, childAges: children > 0 ? childAges : [] }],
    nationality,
    checkIn: checkInDate,
    night: nights,
    currency,
    culture: "en-US"
  };

  try {
    const response = await axios.post(
      `${API_BASE_URL}/productservice/pricesearch`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    const hotels = response.data.body?.hotels;
    if (!Array.isArray(hotels) || !hotels.length) {
      console.warn(`⚠️ No hotels found for City: ${cityName || cityId}`);
      return null;
    }

    // 🔹 Apply global markup to every offer's price
    hotels.forEach((hotel) => {
      if (Array.isArray(hotel.offers)) {
        hotel.offers.forEach((offer) => {
          if (offer.price && typeof offer.price.amount === "number") {
            offer.price.amount = applyPriceMarkup(offer.price.amount);
          }
        });
      }
    });

    const result = {
      searchId: response.data.body.searchId,
      hotels
    };

    // cache & initialize its use count
    hotelOffersCache.set(key, result);
    hotelOffersCacheCount.set(key, 0);

    return result;
  } catch (error) {
    console.error(
      `❌ Error fetching hotels for City: ${cityName || cityId}:`,
      error.response?.data || error.message
    );
    return null;
  }
};

// ✅ Fetch Hotel Details
export const fetchHotelDetails = async (hotelId, provider = 2, culture = "en-US") => {
  const token = await authenticateTourVisio();
  if (!token) {
    console.warn(`⚠️ Auth failed for fetchHotelDetails(${hotelId})`);
    return null;
  }

  const payload = {
    productType: 2,
    ownerProvider: provider,
    product: hotelId,
    culture
  };

  try {
    const response = await axios.post(
      `${API_BASE_URL}/productservice/getproductinfo`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    return response.data.body?.hotel || null;
  } catch (error) {
    console.error(
      `❌ Error fetching details for hotel ${hotelId}:`,
      error.response?.data || error.message
    );
    return null;
  }
};

// ✅ Fetch Hotel Offers Details
export const fetchHotelOffersDetails = async ({
  searchId,
  offerId,
  productType = 2,
  productId,
  currency = "EUR",
  culture = "en-US",
  getRoomInfo = true
}) => {
  const token = await authenticateTourVisio();
  if (!token) {
    console.warn(`⚠️ Auth failed for fetchHotelOffersDetails(${productId})`);
    return null;
  }

  const payload = { searchId, offerId, productType, productId, currency, culture, getRoomInfo };

  try {
    const response = await axios.post(
      `${API_BASE_URL}/productservice/getoffers`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    const body = response.data.body || null;

    if (body && Array.isArray(body.offers)) {
      body.offers.forEach((offer) => {
        if (offer.price && typeof offer.price.amount === "number") {
          offer.price.amount = applyPriceMarkup(offer.price.amount);
        }
      });
    }

    return body;
  } catch (error) {
    console.error(
      `❌ Error fetching offers for hotel ${productId}:`,
      error.response?.data || error.message
    );
    return null;
  }
};

// ✅ Fetch Hotel Price Details
export const fetchHotelPriceDetails = async ({
  hotelId,
  nationality = "XK",
  checkInDate,
  nights = 1,
  currency = "EUR"
}) => {
  const token = await authenticateTourVisio();
  if (!token) return null;

  const payload = {
    checkAllotment: true,
    checkStopSale: true,
    getOnlyDiscountedPrice: false,
    getOnlyBestOffers: true,
    productType: 2,
    Products: [hotelId],
    productPriceCategories: [{ product: hotelId, category: "a" }],
    roomCriteria: [{ adult: 2 }],
    nationality,
    checkIn: checkInDate,
    night: nights,
    currency,
    culture: "en-US"
  };

  try {
    const response = await axios.post(
      `${API_BASE_URL}/productservice/pricesearch`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    const hotels = response.data.body?.hotels;
    if (!hotels?.length) return null;
    const rawPrice = hotels[0].offers?.[0]?.price?.amount || 0;
    return {
      Price: applyPriceMarkup(rawPrice) || "N/A",
      Currency: currency
    };
  } catch (error) {
    console.error(
      `❌ Error fetching price details for hotel ${hotelId}:`,
      error.response?.data || error.message
    );
    return null;
  }
};

// ✅ Commit Transaction
export const commitTransaction = async (transactionId) => {
  const token = await authenticateTourVisio();
  if (!token) throw new Error("Authentication failed");
  const response = await axios.post(
    `${API_BASE_URL}/bookingservice/committransaction`,
    { transactionId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    }
  );
  return response.data.body;
};

// ✅ Get Reservation Details
export const getReservationDetails = async (reservationNumber) => {
  const token = await authenticateTourVisio();
  if (!token) return null;
  try {
    const response = await axios.post(
      `${API_BASE_URL}/bookingservice/getreservationdetail`,
      { reservationNumber },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    return response.data || null;
  } catch (error) {
    console.error(
      `❌ Error fetching reservation details for ${reservationNumber}:`,
      error.response?.data || error.message
    );
    return null;
  }
};
